import React from 'react';
import { Link } from 'react-router-dom';
import './Pricing.css';

const Pricing: React.FC = () => {
  return (
    <div className="pricing">
      <section className="page-hero">
        <div className="container">
          <h1>Simple, Transparent Pricing</h1>
          <p className="subtitle">Pay only for qualified calls. No monthly fees, no contracts.</p>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="pricing-tiers">
            <div className="pricing-tier">
              <h2>Standard Calls</h2>
              <div className="price-display">$5<span>/call</span></div>
              <p className="tier-description">Social media traffic from 14M+ followers</p>
              <ul className="features-list">
                <li>Real-time call routing</li>
                <li>High-intent shoppers</li>
                <li>Lead ownership (follow-ups FREE)</li>
                <li>Call recordings included</li>
                <li>Dashboard access</li>
                <li>Email support</li>
              </ul>
              <Link to="/contact" className="btn btn-primary">Get Started</Link>
            </div>

            <div className="pricing-tier featured">
              <div className="popular-badge">Most Popular</div>
              <h2>Premium Calls</h2>
              <div className="price-display">$15<span>/call</span></div>
              <p className="tier-description">Email list traffic from 10K+ engaged subscribers</p>
              <ul className="features-list">
                <li>Higher conversion rates</li>
                <li>More qualified leads</li>
                <li>Priority routing</li>
                <li>Lead ownership (follow-ups FREE)</li>
                <li>Call recordings included</li>
                <li>Priority support</li>
              </ul>
              <Link to="/contact" className="btn btn-primary">Get Started</Link>
            </div>
          </div>
        </div>
      </section>

      <section className="section gray">
        <div className="container">
          <h2>What Makes a Call Billable?</h2>
          <p className="intro-text">
            We only charge for calls that meet ALL of the following criteria. Your success is our success.
          </p>
          <div className="criteria-grid">
            <div className="criteria-card billable">
              <h3>✓ Billable Calls</h3>
              <div className="criteria-list">
                <div className="criteria-item">
                  <strong>Duration: 90+ seconds</strong>
                  <p>The call must last at least 90 seconds to ensure a meaningful conversation.</p>
                </div>
                <div className="criteria-item">
                  <strong>Cruise-related inquiry</strong>
                  <p>Customer must be asking about cruise bookings, pricing, or availability.</p>
                </div>
                <div className="criteria-item">
                  <strong>Legitimate customer</strong>
                  <p>Real person with genuine intent. Not spam, robocalls, or pranks.</p>
                </div>
                <div className="criteria-item">
                  <strong>First contact from number</strong>
                  <p>The first call from this phone number. Subsequent calls are yours FREE.</p>
                </div>
                <div className="criteria-item">
                  <strong>Successfully connected</strong>
                  <p>You answered the call and had a conversation with the customer.</p>
                </div>
              </div>
            </div>

            <div className="criteria-card not-billable">
              <h3>✗ Not Billable</h3>
              <div className="criteria-list">
                <div className="criteria-item">
                  <strong>Calls under 90 seconds</strong>
                  <p>Quick disconnects, wrong numbers, or very brief conversations.</p>
                </div>
                <div className="criteria-item">
                  <strong>Non-cruise inquiries</strong>
                  <p>Calls about hotels, flights, car rentals, or other non-cruise services.</p>
                </div>
                <div className="criteria-item">
                  <strong>Spam or robocalls</strong>
                  <p>Automated calls, telemarketers, or obviously fake inquiries.</p>
                </div>
                <div className="criteria-item">
                  <strong>Duplicate calls</strong>
                  <p>Repeat calls from the same number within 24 hours.</p>
                </div>
                <div className="criteria-item">
                  <strong>Technical issues</strong>
                  <p>Dropped calls, connection problems, or system errors on our end.</p>
                </div>
                <div className="criteria-item">
                  <strong>Follow-up calls</strong>
                  <p>All subsequent calls from customers assigned to you are FREE forever!</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <h2>Billing & Payment</h2>
          <div className="billing-grid">
            <div className="billing-card">
              <h3>Billing Schedule</h3>
              <p>Automatic monthly billing on the 1st of each month for the previous month's calls.</p>
            </div>
            <div className="billing-card">
              <h3>Payment Method</h3>
              <p>Credit or debit card via Stripe. Secure, automatic, and hassle-free.</p>
            </div>
            <div className="billing-card">
              <h3>No Contracts</h3>
              <p>Month-to-month. Cancel anytime. No long-term commitments or cancelation fees.</p>
            </div>
            <div className="billing-card">
              <h3>Detailed Invoices</h3>
              <p>Complete breakdown of all charges with call recordings and timestamps.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="section gray">
        <div className="container">
          <h2>How We Compare</h2>
          <div className="comparison-table-wrapper">
            <table className="comparison-table">
              <thead>
                <tr>
                  <th></th>
                  <th className="highlight-col">Cruise Spotter Connect</th>
                  <th>Facebook Ads</th>
                  <th>Lead Aggregators</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>Monthly Cost</strong></td>
                  <td className="highlight-col">$0 base fee</td>
                  <td>$500-2000+</td>
                  <td>$500-1500+</td>
                </tr>
                <tr>
                  <td><strong>Per Lead Cost</strong></td>
                  <td className="highlight-col">$5-15/call</td>
                  <td>$30-100+/lead</td>
                  <td>$15-50/lead</td>
                </tr>
                <tr>
                  <td><strong>Lead Quality</strong></td>
                  <td className="highlight-col">10-30% conversion</td>
                  <td>2-8% conversion</td>
                  <td>3-10% conversion</td>
                </tr>
                <tr>
                  <td><strong>Exclusivity</strong></td>
                  <td className="highlight-col">Real-time, yours forever</td>
                  <td>Exclusive (if you convert)</td>
                  <td>Shared with 3-10 agents</td>
                </tr>
                <tr>
                  <td><strong>Setup Time</strong></td>
                  <td className="highlight-col">Hours</td>
                  <td>Weeks (learning curve)</td>
                  <td>Days</td>
                </tr>
                <tr>
                  <td><strong>Contracts</strong></td>
                  <td className="highlight-col">None</td>
                  <td>None (but sunk costs)</td>
                  <td>Often 3-6 months</td>
                </tr>
                <tr>
                  <td><strong>Control</strong></td>
                  <td className="highlight-col">Toggle on/off anytime</td>
                  <td>Full control (complex)</td>
                  <td>Limited</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <h2>Frequently Asked Pricing Questions</h2>
          <div className="faq-list">
            <div className="faq-item">
              <h3>How do I control my spending?</h3>
              <p>
                Toggle your availability to "Unavailable" in the app anytime. When unavailable,
                you won't receive any calls and won't be charged. You have complete control.
              </p>
            </div>
            <div className="faq-item">
              <h3>What if I dispute a charge?</h3>
              <p>
                Every call is recorded for quality assurance. If you believe you were charged
                incorrectly, contact our billing team within 15 days with the call details. We'll
                review the recording and issue a credit if appropriate. See our{' '}
                <Link to="/refund-policy">Refund & Dispute Policy</Link> for details.
              </p>
            </div>
            <div className="faq-item">
              <h3>Are follow-up calls really free?</h3>
              <p>
                Yes! Once you take a call from a customer, all future calls from that phone number
                are routed to you at no charge. Build relationships and earn repeat business without
                paying again.
              </p>
            </div>
            <div className="faq-item">
              <h3>Is there a minimum commitment?</h3>
              <p>
                No. There are no monthly minimums, no contracts, and no long-term commitments. If
                you don't receive any calls in a month, you pay $0.
              </p>
            </div>
            <div className="faq-item">
              <h3>What payment methods do you accept?</h3>
              <p>
                We accept all major credit and debit cards via Stripe. Payment is automatic on the
                1st of each month.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="section cta-section">
        <div className="container">
          <h2>Ready to Get Started?</h2>
          <p>Join our network and start receiving high-quality cruise leads today.</p>
          <Link to="/contact" className="btn btn-large btn-primary">Apply Now</Link>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
